import { Trans, useTranslation } from 'react-i18next';
import Button, {
  ButtonSizes,
  ButtonTypes,
} from '@lib/components/Button/Button';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import RadioButtonGroup from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import { Option } from '@lib/interfaces/form';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import CreditsFooter from './CreditsFooter';
import styles from './AiFieldAssist.module.scss';

type Props = {
  codes: string[];
  selectedCode: string | null;
  // View/List usage (showManualCode on) surfaces a Save button and only
  // updates the local selection on pick; the form (off) auto-applies on pick.
  showManualCode: boolean;
  isSavingManualCode?: boolean;
  onSelect: (code: string) => void;
  onCancel: () => void;
  onApply: () => void;
  creditsRemaining?: AiCredits | null;
};

// ── Multiple candidates: radio list, re-pickable until Dismiss ──
export default function CodePickerContent({
  codes,
  selectedCode,
  showManualCode,
  isSavingManualCode,
  onSelect,
  onCancel,
  onApply,
  creditsRemaining,
}: Props) {
  const { t } = useTranslation();
  const options: Option[] = codes.map((code) => ({ value: code, label: code }));
  const selectedOption = selectedCode
    ? { value: selectedCode, label: selectedCode }
    : undefined;
  return (
    <div className={styles.popoverBody}>
      <div className={styles.popoverHeader}>
        <div className={styles.popoverMessage}>
          <MaterialIcon icon="help" symbolsOutlined size="md-20" />
          <Typography variant="body">
            <Trans i18nKey="ai-multiple-codes-message" />
          </Typography>
        </div>
        <div className={styles.popoverActions}>
          <Button
            buttonText={t('dismiss')}
            buttonType={ButtonTypes.secondaryOutlined}
            buttonSize={ButtonSizes.small}
            onClick={onCancel}
          />
          {showManualCode && (
            <Button
              buttonText={t('apply')}
              buttonType={ButtonTypes.primaryFilled}
              buttonSize={ButtonSizes.small}
              leftIcon="check"
              disabled={!selectedCode}
              isLoading={isSavingManualCode}
              onClick={onApply}
            />
          )}
        </div>
      </div>
      <div className={styles.popoverContent}>
        <Typography variant="body">{t('ai-choose-correct-code')}</Typography>
        <RadioButtonGroup
          name="ai-code-selection"
          options={options}
          value={selectedOption}
          onChange={(option) => onSelect(String(option.value))}
        />
      </div>
      <CreditsFooter credits={creditsRemaining} />
    </div>
  );
}
